/**
 * Typed query hooks for `/api/bi/...` endpoints.
 *
 * Convention: each hook is a thin wrapper over `useQuery` with a stable
 * key including all filters that affect the result (so cache stays correct
 * when the user toggles range/store).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { apiClient, get, post } from '../client';
import { storage } from '@/lib/storage';
import { TIMEZONE } from '@/lib/format';
import { useSettingsStore } from './settings';
import type { TimeRangeMode } from '@/state/filters';

/**
 * Live KPI refresh cadence (ms). Reads first from the server-backed
 * settings store, falling back to the local cache so cold-boot queries
 * still poll at the user's preferred interval before the auth bootstrap
 * finishes. `false` disables polling entirely in React Query.
 */
function refetchInterval(): number | false {
  const fromServer = useSettingsStore.getState().blob?.prefs?.refresh_ms;
  const fromCache = storage.getPrefs().refresh_ms;
  const ms = typeof fromServer === 'number' ? fromServer : fromCache;
  return ms > 0 ? ms : false;
}
const REFETCH_INTERVAL: () => number | false = refetchInterval;
import type {
  ShrinkageAnomaliesResponse,
  ShrinkageAttributionResponse,
  ShrinkageHotspotsResponse,
  ShrinkageOverviewResponse,
  ShrinkageVarianceResponse,
} from './types';
import type {
  CartAbandonmentResponse,
  CouponDetailResponse,
  CouponPerformanceResponse,
  DamageSummaryResponse,
  InventoryHealthResponse,
  LedgerBalanceResponse,
  LedgerCondition,
  LedgerMovementType,
  LedgerSummaryResponse,
  OrderCancellationsResponse,
  OrderDensityResponse,
  OrderFunnelResponse,
  OrderPointsResponse,
  LateHeatmapResponse,
  PolygonRing,
  SitingSource,
  SitingSelectionResponse,
  SitingCandidateResponse,
  SitingSimulationResponse,
  SitingBifurcationResponse,
  SitingRouteTestResponse,
  OrderSlaContributorsResponse,
  OrderSlaResponse,
  OrdersDurationsResponse,
  OrdersSlaHeatmapResponse,
  OrdersTimeseriesResponse,
  OverviewResponse,
  PaginatedLedgerResponse,
  PaymentMixResponse,
  PaymentsFunnelResponse,
  PaymentsMandatesResponse,
  FeedbackNpsResponse,
  NotificationsCampaignsResponse,
  NotificationsAttributionResponse,
  AlertRulesResponse,
  AlertHistoryResponse,
  AlertEvent,
  AlertRule,
  AlertSeverity,
  AlertKind,
  AuditFeedResponse,
  ContributorMetric,
  OverviewContributorsResponse,
  ReportFormat,
  ReportFrequency,
  ReportSchedule,
  ReportSchedulesResponse,
  TrialBalanceResponse,
  RosterCoverageResponse,
  RFMResponse,
  ReferralDetailResponse,
  ReferralPerformanceResponse,
  RiderUtilizationResponse,
  RidersDistributionResponse,
  SearchInsightsResponse,
  SearchZeroResultsResponse,
  StockoutsAggregateResponse,
  StockoutsResponse,
  StoreComparisonResponse,
  StoreGeofencesResponse,
  UserCohortsResponse,
  UserFunnelResponse,
  VendorGrnDetailResponse,
  VendorLeaderboardResponse,
  VendorPoDetailResponse,
  VendorPosListResponse,
  VendorProductSalesResponse,
  VendorProfitabilityQuadrantResponse,
  VendorProfitabilityResponse,
  VendorSubcategoryDecisionListResponse,
  VendorSubcategoryDecisionRow,
  VendorDecision,
  ProcurementHealthResponse,
  InventoryForecastResponse,
  ReplenishmentLevelsResponse,
  ReplenishmentBasketsResponse,
  ReplenishmentTransfersResponse,
  ReplenishmentLevelStatus,
  WaitlistDemandResponse,
  WishlistDemandResponse,
  Granularity,
  ProductGroupBy,
  ProductPerformanceResponse,
  ProductTimeseriesResponse,
  ProductForecastResponse,
  ProductQuadrantResponse,
  ProductDrilldownResponse,
  ProductRepeatResponse,
  ProductStoreComparisonResponse,
  ProductAffinityResponse,
  ProductVelocityResponse,
  ProductWardSalesResponse,
  WardDetailResponse,
  WardsGeoResponse,
} from './types';

interface BaseFilters {
  start: string;
  end: string;
  storeId?: number | null;
  storeIds?: number[] | null;
  timeRangeMode?: TimeRangeMode;
  compareStart?: string | null;
  compareEnd?: string | null;
  enabled?: boolean;
}

type DateBoundary = 'start' | 'end';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeBIDateParam(
  value: string,
  boundary: DateBoundary,
  timeRangeMode: TimeRangeMode = 'standard',
): string {
  if (!DATE_ONLY_RE.test(value)) return value;
  if (timeRangeMode === 'business') {
    if (boundary === 'start') {
      return toDate(`${value}T06:00:00.000`, { timeZone: TIMEZONE }).toISOString();
    }
    const dayAfter = formatInTimeZone(
      addDays(toDate(`${value}T00:00:00`, { timeZone: TIMEZONE }), 1),
      TIMEZONE,
      'yyyy-MM-dd',
    );
    return toDate(`${dayAfter}T05:59:59.999`, { timeZone: TIMEZONE }).toISOString();
  }
  const suffix = boundary === 'start' ? 'T00:00:00.000' : 'T23:59:59.999';
  return toDate(`${value}${suffix}`, { timeZone: TIMEZONE }).toISOString();
}

export function biRangeParams(f: Pick<BaseFilters, 'start' | 'end' | 'timeRangeMode'>) {
  return {
    start: normalizeBIDateParam(f.start, 'start', f.timeRangeMode),
    end: normalizeBIDateParam(f.end, 'end', f.timeRangeMode),
  };
}

function paramsFor(f: BaseFilters, extra: Record<string, string | number | undefined> = {}) {
  let storeParam: string | number | undefined;
  if (f.storeIds && f.storeIds.length > 0) {
    storeParam = f.storeIds.join(',');
  } else if (f.storeId != null) {
    storeParam = f.storeId;
  }
  return {
    ...biRangeParams(f),
    ...(storeParam !== undefined ? { store_id: storeParam } : {}),
    ...(f.compareStart && f.compareEnd
      ? {
          compare_start: normalizeBIDateParam(f.compareStart, 'start', f.timeRangeMode),
          compare_end: normalizeBIDateParam(f.compareEnd, 'end', f.timeRangeMode),
        }
      : {}),
    ...extra,
  };
}

/**
 * Coalesce a multi-select id field (`fooIds: number[]`) and its legacy
 * single-select sibling (`fooId: number`) into the csv string the BI
 * endpoints accept (`?foo_id=1,2,3`). Returns `undefined` when both are
 * empty so the caller can spread the result without polluting the query.
 */
function csvIdParam(
  ids: number[] | null | undefined,
  legacy: number | null | undefined,
): string | undefined {
  if (ids && ids.length > 0) return ids.join(',');
  if (legacy != null) return String(legacy);
  return undefined;
}

function csvIdKey(
  ids: number[] | null | undefined,
  legacy: number | null | undefined,
): string {
  // Sort so [1,2] and [2,1] hit the same React Query cache slot.
  if (ids && ids.length > 0) return [...ids].sort((a, b) => a - b).join(',');
  if (legacy != null) return String(legacy);
  return '';
}

const ENABLED_DEFAULT = (f: BaseFilters) => f.enabled !== false && Boolean(f.start && f.end);

export function useOverview(f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'overview',
      f.start,
      f.end,
      f.compareStart ?? null,
      f.compareEnd ?? null,
      f.storeId ?? null,
    ],
    queryFn: () => get<OverviewResponse>('/api/bi/overview/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useOrdersTimeseries(f: BaseFilters & { granularity?: Granularity }) {
  return useQuery({
    queryKey: [
      'bi',
      'orders-timeseries',
      f.start,
      f.end,
      f.storeId ?? null,
      f.granularity ?? 'day',
      f.enabled ?? true,
    ],
    queryFn: () =>
      get<OrdersTimeseriesResponse>('/api/bi/orders/timeseries/', {
        params: paramsFor(f, { granularity: f.granularity ?? 'day' }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useOrderFunnel(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'orders-funnel', f.start, f.end, f.storeId ?? null],
    queryFn: () => get<OrderFunnelResponse>('/api/bi/orders/funnel/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useOrderSla(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'orders-sla', f.start, f.end, f.storeId ?? null],
    queryFn: () => get<OrderSlaResponse>('/api/bi/orders/sla/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useOrderSlaContributors(
  f: BaseFilters & { granularity?: Granularity; limit?: number },
) {
  return useQuery({
    queryKey: [
      'bi',
      'orders-sla-contributors',
      f.start,
      f.end,
      f.storeId ?? null,
      f.granularity ?? 'day',
      f.limit ?? 5,
    ],
    queryFn: () =>
      get<OrderSlaContributorsResponse>('/api/bi/orders/sla/contributors/', {
        params: paramsFor(f, {
          granularity: f.granularity ?? 'day',
          limit: f.limit ?? 5,
        }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useOrderCancellations(f: BaseFilters & { page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: [
      'bi',
      'orders-cancellations',
      f.start,
      f.end,
      f.storeId ?? null,
      f.page ?? 1,
      f.pageSize ?? 10,
    ],
    queryFn: () =>
      get<OrderCancellationsResponse>('/api/bi/orders/cancellations/', {
        params: paramsFor(f, {
          page: f.page ?? 1,
          page_size: f.pageSize ?? 10,
        }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

interface OrderPointsFilters extends Partial<BaseFilters> {
  start?: string;
  end?: string;
  status?: string;
  search?: string;
  /** Short-circuits the date filter; used by the rider trip popover. */
  orderIds?: number[];
  limit?: number;
  enabled?: boolean;
}

/**
 * Slim per-order coordinates feed used by:
 *   - the orders map view (range/status/store/search filters)
 *   - the rider trip popover (`orderIds` short-circuit)
 *
 * When `orderIds` is provided the date range is ignored server-side, so
 * the caller can omit `start`/`end` entirely.
 */
export function useOrdersPoints(f: OrderPointsFilters) {
  const { status, search, limit, enabled, orderIds, ...base } = f;
  const usingIds = (orderIds?.length ?? 0) > 0;
  const sortedIds = orderIds && orderIds.length ? [...orderIds].sort((a, b) => a - b) : null;

  return useQuery({
    queryKey: [
      'bi',
      'orders-points',
      base.start ?? null,
      base.end ?? null,
      csvIdKey(base.storeIds, base.storeId),
      status ?? '',
      search ?? '',
      sortedIds ? sortedIds.join(',') : '',
      limit ?? null,
    ],
    queryFn: () => {
      const extra: Record<string, string | number> = {};
      if (status) extra.status = status;
      if (search) extra.search = search;
      if (limit) extra.limit = limit;
      if (sortedIds) extra.order_ids = sortedIds.join(',');
      const params = paramsFor(base as BaseFilters, extra);
      // When fetching by id we don't need a range — drop the empty
      // `start`/`end` to avoid the BI parser yelling.
      if (usingIds) {
        delete (params as Record<string, unknown>).start;
        delete (params as Record<string, unknown>).end;
      }
      return get<OrderPointsResponse>('/api/bi/orders/points/', { params });
    },
    enabled:
      enabled !== false && (usingIds || Boolean(base.start && base.end)),
    refetchInterval: usingIds ? false : REFETCH_INTERVAL,
  });
}

export function useUserFunnel(f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'users-funnel',
      f.start,
      f.end,
      f.compareStart ?? null,
      f.compareEnd ?? null,
    ],
    queryFn: () => get<UserFunnelResponse>('/api/bi/users/funnel/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useUserCohorts(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'users-cohorts', f.start, f.end],
    queryFn: () => get<UserCohortsResponse>('/api/bi/users/cohorts/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useInventoryHealth(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'inventory-health', f.start, f.end, f.storeId ?? null],
    queryFn: () =>
      get<InventoryHealthResponse>('/api/bi/inventory/health/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function usePaymentMix(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'payment-mix', f.start, f.end, f.storeId ?? null],
    queryFn: () => get<PaymentMixResponse>('/api/bi/payments/mix/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useStoreComparison(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'stores-comparison', f.start, f.end],
    queryFn: () =>
      get<StoreComparisonResponse>('/api/bi/stores/comparison/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useRiderUtilization(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'riders-utilization', f.start, f.end, f.storeId ?? null],
    queryFn: () =>
      get<RiderUtilizationResponse>('/api/bi/riders/utilization/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

// ---------------------------------------------------------------------------
// Stock ledger
// ---------------------------------------------------------------------------

export interface LedgerFilters extends BaseFilters {
  productId?: number | null;
  productIds?: number[] | null;
  subcategoryId?: number | null;
  subcategoryIds?: number[] | null;
  categoryId?: number | null;
  categoryIds?: number[] | null;
  categoryGroupId?: number | null;
  categoryGroupIds?: number[] | null;
  brandId?: number | null;
  brandIds?: number[] | null;
  vendorId?: number | null;
  vendorIds?: number[] | null;
  movementType?: LedgerMovementType | LedgerMovementType[] | 'damage' | null;
  condition?: LedgerCondition | 'all' | null;
  q?: string;
  page?: number;
  pageSize?: number;
}

function ledgerParams(f: LedgerFilters) {
  const movementType = Array.isArray(f.movementType)
    ? f.movementType.join(',')
    : f.movementType ?? undefined;
  const productParam = csvIdParam(f.productIds, f.productId);
  const subcategoryParam = csvIdParam(f.subcategoryIds, f.subcategoryId);
  const categoryParam = csvIdParam(f.categoryIds, f.categoryId);
  const categoryGroupParam = csvIdParam(f.categoryGroupIds, f.categoryGroupId);
  const brandParam = csvIdParam(f.brandIds, f.brandId);
  const vendorParam = csvIdParam(f.vendorIds, f.vendorId);
  return paramsFor(f, {
    ...(productParam !== undefined ? { product_id: productParam } : {}),
    ...(subcategoryParam !== undefined ? { subcategory_id: subcategoryParam } : {}),
    ...(categoryParam !== undefined ? { category_id: categoryParam } : {}),
    ...(categoryGroupParam !== undefined ? { category_group_id: categoryGroupParam } : {}),
    ...(brandParam !== undefined ? { brand_id: brandParam } : {}),
    ...(vendorParam !== undefined ? { vendor_id: vendorParam } : {}),
    ...(movementType ? { movement_type: movementType } : {}),
    ...(f.condition && f.condition !== 'all' ? { condition: f.condition } : {}),
    ...(f.q ? { q: f.q } : {}),
    ...(f.page ? { page: f.page } : {}),
    ...(f.pageSize ? { page_size: f.pageSize } : {}),
  });
}

function ledgerKey(prefix: string, f: LedgerFilters): unknown[] {
  return [
    'bi', prefix, f.start, f.end,
    csvIdKey(f.storeIds, f.storeId),
    csvIdKey(f.productIds, f.productId),
    csvIdKey(f.subcategoryIds, f.subcategoryId),
    csvIdKey(f.categoryIds, f.categoryId),
    csvIdKey(f.categoryGroupIds, f.categoryGroupId),
    csvIdKey(f.brandIds, f.brandId),
    csvIdKey(f.vendorIds, f.vendorId),
    Array.isArray(f.movementType) ? f.movementType.join(',') : (f.movementType ?? null),
    f.condition ?? null, f.q ?? '',
    f.page ?? 1, f.pageSize ?? 50,
  ];
}

export function useStockLedger(f: LedgerFilters) {
  return useQuery({
    queryKey: ledgerKey('stock-ledger', f),
    queryFn: () =>
      get<PaginatedLedgerResponse>('/api/bi/inventory/ledger/', { params: ledgerParams(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useStockLedgerSummary(f: LedgerFilters) {
  return useQuery({
    queryKey: ledgerKey('stock-ledger-summary', { ...f, page: undefined, pageSize: undefined }),
    queryFn: () =>
      get<LedgerSummaryResponse>('/api/bi/inventory/ledger/summary/', {
        params: ledgerParams({ ...f, page: undefined, pageSize: undefined }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useStockLedgerBalance(
  f: BaseFilters & { condition?: LedgerCondition | 'all' | null; page?: number; pageSize?: number },
) {
  return useQuery({
    queryKey: [
      'bi', 'stock-ledger-balance',
      f.end, f.storeId ?? null, f.condition ?? null, f.page ?? 1, f.pageSize ?? 50,
    ],
    queryFn: () =>
      get<LedgerBalanceResponse>('/api/bi/inventory/ledger/balance/', {
        params: paramsFor(f, {
          ...(f.condition && f.condition !== 'all' ? { condition: f.condition } : {}),
          ...(f.page ? { page: f.page } : {}),
          ...(f.pageSize ? { page_size: f.pageSize } : {}),
        }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export interface StockoutFilters extends BaseFilters {
  productId?: number | null;
  productIds?: number[] | null;
  subcategoryId?: number | null;
  subcategoryIds?: number[] | null;
  categoryId?: number | null;
  categoryIds?: number[] | null;
  categoryGroupId?: number | null;
  categoryGroupIds?: number[] | null;
  brandId?: number | null;
  brandIds?: number[] | null;
  vendorId?: number | null;
  vendorIds?: number[] | null;
  q?: string;
  page?: number;
  pageSize?: number;
  currentlyOutOnly?: boolean;
  minTtrHours?: number;
}

function stockoutParams(f: StockoutFilters) {
  const productParam = csvIdParam(f.productIds, f.productId);
  const subcategoryParam = csvIdParam(f.subcategoryIds, f.subcategoryId);
  const categoryParam = csvIdParam(f.categoryIds, f.categoryId);
  const categoryGroupParam = csvIdParam(f.categoryGroupIds, f.categoryGroupId);
  const brandParam = csvIdParam(f.brandIds, f.brandId);
  const vendorParam = csvIdParam(f.vendorIds, f.vendorId);
  return paramsFor(f, {
    ...(productParam !== undefined ? { product_id: productParam } : {}),
    ...(subcategoryParam !== undefined ? { subcategory_id: subcategoryParam } : {}),
    ...(categoryParam !== undefined ? { category_id: categoryParam } : {}),
    ...(categoryGroupParam !== undefined ? { category_group_id: categoryGroupParam } : {}),
    ...(brandParam !== undefined ? { brand_id: brandParam } : {}),
    ...(vendorParam !== undefined ? { vendor_id: vendorParam } : {}),
    ...(f.q ? { q: f.q } : {}),
    ...(f.page ? { page: f.page } : {}),
    ...(f.pageSize ? { page_size: f.pageSize } : {}),
    ...(f.currentlyOutOnly ? { currently_out_only: 1 } : {}),
    ...(f.minTtrHours != null ? { min_ttr_hours: f.minTtrHours } : {}),
  });
}

function stockoutKey(prefix: string, f: StockoutFilters): unknown[] {
  return [
    'bi', prefix, f.start, f.end,
    csvIdKey(f.storeIds, f.storeId),
    csvIdKey(f.productIds, f.productId),
    csvIdKey(f.subcategoryIds, f.subcategoryId),
    csvIdKey(f.categoryIds, f.categoryId),
    csvIdKey(f.categoryGroupIds, f.categoryGroupId),
    csvIdKey(f.brandIds, f.brandId),
    csvIdKey(f.vendorIds, f.vendorId),
    f.q ?? '',
    f.page ?? 1, f.pageSize ?? 50, !!f.currentlyOutOnly, f.minTtrHours ?? null,
  ];
}

export function useStockouts(f: StockoutFilters) {
  return useQuery({
    queryKey: stockoutKey('stockouts', f),
    queryFn: () =>
      get<StockoutsResponse>('/api/bi/inventory/stockouts/', { params: stockoutParams(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useStockoutAggregate(f: StockoutFilters) {
  return useQuery({
    queryKey: stockoutKey('stockouts-aggregate', { ...f, page: undefined, pageSize: undefined }),
    queryFn: () =>
      get<StockoutsAggregateResponse>('/api/bi/inventory/stockouts/aggregate/', {
        params: stockoutParams({ ...f, page: undefined, pageSize: undefined }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

// ---------------------------------------------------------------------------
// Taxonomy (brands / categories / subcategories) — used by filter selects.
// Cached for 10 minutes server-side; we reuse the same on the client.
// ---------------------------------------------------------------------------

export interface TaxonomyBrand { id: number; name: string }
export interface TaxonomyCategoryGroup { id: number; name: string; display_order: number | null }
export interface TaxonomyCategory {
  id: number;
  name: string;
  category_group_id: number | null;
  display_order: number | null;
}
export interface TaxonomySubCategory {
  id: number;
  name: string;
  display_order: number | null;
  category_ids: number[];
}
export interface TaxonomyVendor {
  id: number;
  name: string;
  /**
   * Subcategories that this vendor stocks (M2M `Vendor.subcategories`).
   * Used by the cascade filter to constrain the Category Group / Category /
   * Sub-Category pickers when one or more vendors are selected.
   */
  subcategory_ids: number[];
  /** Brands the vendor carries (M2M `Vendor.brands`). */
  brand_ids: number[];
}
export interface InventoryTaxonomyResponse {
  brands: TaxonomyBrand[];
  category_groups: TaxonomyCategoryGroup[];
  categories: TaxonomyCategory[];
  subcategories: TaxonomySubCategory[];
  vendors: TaxonomyVendor[];
}

export function useInventoryTaxonomy() {
  return useQuery({
    // v2: payload now includes `vendors` with brand_ids/subcategory_ids
    // — bump so existing clients re-fetch and don't blow up reading
    // `vendors` off an undefined.
    queryKey: ['bi', 'inventory-taxonomy', 'v2'],
    queryFn: () =>
      get<InventoryTaxonomyResponse>('/api/bi/inventory/taxonomy/'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useDamageSummary(f: LedgerFilters) {
  return useQuery({
    queryKey: ledgerKey('damage-summary', { ...f, page: undefined, pageSize: undefined }),
    queryFn: () =>
      get<DamageSummaryResponse>('/api/bi/inventory/damage/', {
        params: ledgerParams({ ...f, page: undefined, pageSize: undefined }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useDamageMovements(f: LedgerFilters) {
  return useQuery({
    queryKey: ledgerKey('damage-movements', f),
    queryFn: () =>
      get<PaginatedLedgerResponse>('/api/bi/inventory/damage/movements/', { params: ledgerParams(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

// ---------------------------------------------------------------------------
// Stage 4 — Merchandising & Demand
// ---------------------------------------------------------------------------

interface ListFilters extends BaseFilters {
  limit?: number;
}

function listKey(name: string, f: ListFilters) {
  return [
    'bi',
    name,
    f.start,
    f.end,
    (f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null),
    f.limit ?? null,
  ];
}

export function useSearchInsights(f: ListFilters) {
  return useQuery({
    queryKey: listKey('search-insights', f),
    queryFn: () =>
      get<SearchInsightsResponse>('/api/bi/search/insights/', {
        params: paramsFor(f, f.limit ? { limit: f.limit } : {}),
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useSearchZeroResults(f: ListFilters) {
  return useQuery({
    queryKey: listKey('search-zero-results', f),
    queryFn: () =>
      get<SearchZeroResultsResponse>('/api/bi/search/zero-results/', {
        params: paramsFor(f, f.limit ? { limit: f.limit } : {}),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

interface WishlistFilters {
  storeId?: number | null;
  storeIds?: number[] | null;
  limit?: number;
}

export function useWishlistDemand(f: WishlistFilters) {
  const params: Record<string, string | number | undefined> = {};
  if (f.storeIds && f.storeIds.length) params.store_id = f.storeIds.join(',');
  else if (f.storeId != null) params.store_id = f.storeId;
  if (f.limit) params.limit = f.limit;
  return useQuery({
    queryKey: [
      'bi',
      'wishlist-demand',
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
      f.limit ?? null,
    ],
    queryFn: () =>
      get<WishlistDemandResponse>('/api/bi/wishlist/demand/', { params }),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useWaitlistDemand(f: ListFilters) {
  return useQuery({
    queryKey: listKey('waitlist-demand', f),
    queryFn: () =>
      get<WaitlistDemandResponse>('/api/bi/waitlist/demand/', {
        params: paramsFor(f, f.limit ? { limit: f.limit } : {}),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useOrderDensity(f: BaseFilters & { precision?: number; limit?: number }) {
  return useQuery({
    queryKey: [
      'bi',
      'orders-density',
      f.start,
      f.end,
      (f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null),
      f.precision ?? 3,
      f.limit ?? null,
    ],
    queryFn: () =>
      get<OrderDensityResponse>('/api/bi/orders/density/', {
        params: paramsFor(f, {
          ...(f.precision ? { precision: f.precision } : {}),
          ...(f.limit ? { limit: f.limit } : {}),
        }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useStoreGeofences() {
  return useQuery({
    queryKey: ['bi', 'store-geofences'],
    queryFn: () => get<StoreGeofencesResponse>('/api/bi/stores/geofences/'),
    staleTime: 10 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Darkstore siting tool
// ---------------------------------------------------------------------------

/**
 * Demand heatmap. For `orders` (default) cells are coloured by late rate;
 * for the user/waitlist sources they're a density of active customers /
 * waitlist requests, so `threshold_minutes` is ignored server-side.
 */
export function useLateHeatmap(
  f: BaseFilters & { thresholdMinutes: number; precision?: number; source?: SitingSource },
) {
  const source = f.source ?? 'orders';
  return useQuery({
    queryKey: [
      'bi',
      'siting-late-heatmap',
      source,
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
      f.thresholdMinutes,
      f.precision ?? 3,
    ],
    queryFn: () =>
      get<LateHeatmapResponse>('/api/bi/siting/late-heatmap/', {
        params: paramsFor(f, {
          source,
          threshold_minutes: f.thresholdMinutes,
          ...(f.precision ? { precision: f.precision } : {}),
        }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

interface SitingPolygonVars {
  polygon: PolygonRing;
  thresholdMinutes: number;
  source?: SitingSource;
  start: string;
  end: string;
  storeIds?: number[] | null;
  storeId?: number | null;
  timeRangeMode?: TimeRangeMode;
}

/** Demand inside a drawn polygon → markers + aggregate stats. POST (polygon in body). */
export function useSitingSelection() {
  return useMutation({
    mutationFn: (vars: SitingPolygonVars & { limit?: number }) =>
      post<SitingSelectionResponse>(
        '/api/bi/siting/selection/',
        {
          polygon: vars.polygon,
          threshold_minutes: vars.thresholdMinutes,
          source: vars.source ?? 'orders',
          ...(vars.limit ? { limit: vars.limit } : {}),
        },
        { params: paramsFor(vars) },
      ),
  });
}

/** Suggested candidate site(s) from the demand inside the polygon. */
export function useSitingCandidate() {
  return useMutation({
    mutationFn: (vars: SitingPolygonVars & { k?: number }) =>
      post<SitingCandidateResponse>(
        '/api/bi/siting/candidate/',
        {
          polygon: vars.polygon,
          threshold_minutes: vars.thresholdMinutes,
          source: vars.source ?? 'orders',
          ...(vars.k ? { k: vars.k } : {}),
        },
        { params: paramsFor(vars) },
      ),
  });
}

interface SitingSimulateVars {
  candidate: { lat: number; lng: number };
  coverageRadiusM?: number | null;
  polygon?: PolygonRing | null;
  thresholdMinutes: number;
  source?: SitingSource;
  start: string;
  end: string;
  storeIds?: number[] | null;
  storeId?: number | null;
  timeRangeMode?: TimeRangeMode;
}

/** Project SLA improvement + load shift for a proposed store location. */
export function useSitingSimulation() {
  return useMutation({
    mutationFn: (vars: SitingSimulateVars) =>
      post<SitingSimulationResponse>(
        '/api/bi/siting/simulate/',
        {
          candidate: vars.candidate,
          threshold_minutes: vars.thresholdMinutes,
          source: vars.source ?? 'orders',
          ...(vars.coverageRadiusM != null ? { coverage_radius_m: vars.coverageRadiusM } : {}),
          ...(vars.polygon ? { polygon: vars.polygon } : {}),
        },
        { params: paramsFor(vars) },
      ),
  });
}

interface SitingBifurcateVars {
  polygon: PolygonRing;
  thresholdMinutes: number;
  source?: SitingSource;
  targetLateRate?: number;
  maxSites?: number;
  minOrdersPerSite?: number;
  start: string;
  end: string;
  storeIds?: number[] | null;
  storeId?: number | null;
  timeRangeMode?: TimeRangeMode;
}

/** Split an area into the fewest store sites that hit the target late-rate. */
export function useSitingBifurcation() {
  return useMutation({
    mutationFn: (vars: SitingBifurcateVars) =>
      post<SitingBifurcationResponse>(
        '/api/bi/siting/bifurcate/',
        {
          polygon: vars.polygon,
          threshold_minutes: vars.thresholdMinutes,
          source: vars.source ?? 'orders',
          ...(vars.targetLateRate != null ? { target_late_rate: vars.targetLateRate } : {}),
          ...(vars.maxSites ? { max_sites: vars.maxSites } : {}),
          ...(vars.minOrdersPerSite ? { min_orders_per_site: vars.minOrdersPerSite } : {}),
        },
        { params: paramsFor(vars) },
      ),
  });
}

export interface LatLngPoint {
  lat: number;
  lng: number;
}

/**
 * Point-to-point route tester for the siting map. Enabled only once both an
 * origin and a destination pin are set; results are cached per rounded
 * coordinate pair so dragging a pin to the same spot reuses the route.
 */
export function useSitingRouteTest(origin: LatLngPoint | null, dest: LatLngPoint | null) {
  const round6 = (n: number) => Math.round(n * 1e6) / 1e6;
  return useQuery({
    queryKey: [
      'bi',
      'siting-route-test',
      origin ? `${round6(origin.lat)},${round6(origin.lng)}` : null,
      dest ? `${round6(dest.lat)},${round6(dest.lng)}` : null,
    ],
    queryFn: () =>
      get<SitingRouteTestResponse>('/api/bi/siting/route-test/', {
        params: {
          origin_lat: origin!.lat,
          origin_lng: origin!.lng,
          dest_lat: dest!.lat,
          dest_lng: dest!.lng,
        },
      }),
    enabled: Boolean(origin && dest),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Stage 5 — Supply & Procurement
// ---------------------------------------------------------------------------

interface VendorLeaderboardFilters extends ListFilters {
  q?: string;
}

export function useVendorLeaderboard(f: VendorLeaderboardFilters) {
  const q = (f.q ?? '').trim();
  return useQuery({
    queryKey: [...listKey('vendor-leaderboard', f), q],
    queryFn: () =>
      get<VendorLeaderboardResponse>('/api/bi/vendors/leaderboard/', {
        params: paramsFor(f, {
          ...(f.limit ? { limit: f.limit } : {}),
          ...(q ? { q } : {}),
        }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

interface VendorPosFilters {
  vendorId: number | null;
  start: string;
  end: string;
  page?: number;
  pageSize?: number;
  status?: string | null;
  onlyOpen?: boolean;
  enabled?: boolean;
}

export function useVendorPos(f: VendorPosFilters) {
  const enabled = f.enabled !== false && Boolean(f.vendorId && f.start && f.end);
  return useQuery({
    queryKey: [
      'bi', 'vendor-pos',
      f.vendorId, f.start, f.end,
      f.page ?? 1, f.pageSize ?? 25,
      f.status ?? null, !!f.onlyOpen,
    ],
    queryFn: () =>
      get<VendorPosListResponse>(`/api/bi/vendors/${f.vendorId}/pos/`, {
        params: {
          ...biRangeParams(f),
          page: f.page ?? 1,
          page_size: f.pageSize ?? 25,
          ...(f.status ? { status: f.status } : {}),
          ...(f.onlyOpen ? { open: 1 } : {}),
        },
      }),
    enabled,
  });
}

export function useVendorPoDetail(poId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['bi', 'vendor-po-detail', poId],
    queryFn: () => get<VendorPoDetailResponse>(`/api/bi/vendors/pos/${poId}/`),
    enabled: enabled && Boolean(poId),
  });
}

export function useVendorGrnDetail(grnId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['bi', 'vendor-grn-detail', grnId],
    queryFn: () => get<VendorGrnDetailResponse>(`/api/bi/vendors/grns/${grnId}/`),
    enabled: enabled && Boolean(grnId),
  });
}

interface VendorProductSalesFilters {
  vendorId: number | null;
  start: string;
  end: string;
  storeIds?: number[] | null;
  brandId?: number | null;
  brandIds?: number[] | null;
  categoryId?: number | null;
  categoryIds?: number[] | null;
  subcategoryId?: number | null;
  subcategoryIds?: number[] | null;
  q?: string;
  limit?: number;
  enabled?: boolean;
}

export function useVendorProductSales(f: VendorProductSalesFilters) {
  const q = (f.q ?? '').trim();
  const storeKey = f.storeIds && f.storeIds.length ? [...f.storeIds].sort().join(',') : '';
  const enabled = f.enabled !== false && Boolean(f.vendorId && f.start && f.end);
  return useQuery({
    queryKey: [
      'bi', 'vendor-product-sales',
      f.vendorId, f.start, f.end, storeKey,
      csvIdKey(f.brandIds, f.brandId),
      csvIdKey(f.categoryIds, f.categoryId),
      csvIdKey(f.subcategoryIds, f.subcategoryId),
      q, f.limit ?? null,
    ],
    queryFn: () => {
      const params: Record<string, string | number> = {
        ...biRangeParams(f),
      };
      if (f.storeIds && f.storeIds.length) params.store_id = f.storeIds.join(',');
      const brandParam = csvIdParam(f.brandIds, f.brandId);
      const categoryParam = csvIdParam(f.categoryIds, f.categoryId);
      const subcategoryParam = csvIdParam(f.subcategoryIds, f.subcategoryId);
      if (brandParam !== undefined) params.brand_id = brandParam;
      if (categoryParam !== undefined) params.category_id = categoryParam;
      if (subcategoryParam !== undefined) params.subcategory_id = subcategoryParam;
      if (q) params.q = q;
      if (f.limit) params.limit = f.limit;
      return get<VendorProductSalesResponse>(
        `/api/bi/vendors/${f.vendorId}/product-sales/`,
        { params },
      );
    },
    enabled,
  });
}

export function useProcurementHealth(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'procurement-health', f.start, f.end],
    queryFn: () =>
      get<ProcurementHealthResponse>('/api/bi/procurement/health/', {
        params: paramsFor(f),
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface InventoryForecastFilters {
  storeId?: number | null;
  storeIds?: number[] | null;
  vendorId?: number | null;
  vendorIds?: number[] | null;
  brandId?: number | null;
  brandIds?: number[] | null;
  categoryId?: number | null;
  categoryIds?: number[] | null;
  categoryGroupId?: number | null;
  categoryGroupIds?: number[] | null;
  subcategoryId?: number | null;
  subcategoryIds?: number[] | null;
  q?: string;
  limit?: number;
  horizon?: number;
}

export function useInventoryForecast(f: InventoryForecastFilters) {
  const params: Record<string, string | number | undefined> = {};
  if (f.storeIds && f.storeIds.length) params.store_id = f.storeIds.join(',');
  else if (f.storeId != null) params.store_id = f.storeId;
  const vendorParam = csvIdParam(f.vendorIds, f.vendorId);
  const brandParam = csvIdParam(f.brandIds, f.brandId);
  const categoryParam = csvIdParam(f.categoryIds, f.categoryId);
  const categoryGroupParam = csvIdParam(f.categoryGroupIds, f.categoryGroupId);
  const subcategoryParam = csvIdParam(f.subcategoryIds, f.subcategoryId);
  if (vendorParam !== undefined) params.vendor_id = vendorParam;
  if (brandParam !== undefined) params.brand_id = brandParam;
  if (categoryParam !== undefined) params.category_id = categoryParam;
  if (categoryGroupParam !== undefined) params.category_group_id = categoryGroupParam;
  if (subcategoryParam !== undefined) params.subcategory_id = subcategoryParam;
  if (f.q && f.q.trim()) params.q = f.q.trim();
  if (f.limit) params.limit = f.limit;
  if (f.horizon) params.horizon = f.horizon;
  return useQuery({
    queryKey: [
      'bi',
      'inventory-forecast',
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
      csvIdKey(f.vendorIds, f.vendorId),
      csvIdKey(f.brandIds, f.brandId),
      csvIdKey(f.categoryIds, f.categoryId),
      csvIdKey(f.categoryGroupIds, f.categoryGroupId),
      csvIdKey(f.subcategoryIds, f.subcategoryId),
      f.q?.trim() || '',
      f.limit ?? null,
      f.horizon ?? 14,
    ],
    queryFn: () =>
      get<InventoryForecastResponse>('/api/bi/inventory/forecast/', { params }),
    refetchInterval: REFETCH_INTERVAL,
  });
}

// ---------------------------------------------------------------------------
// Replenishment lifecycle (per-store levels, warehouse staging, inbound)
// ---------------------------------------------------------------------------

export interface ReplenishmentLevelsFilters {
  storeId?: number | null;
  storeIds?: number[] | null;
  productId?: number | null;
  productIds?: number[] | null;
  subcategoryId?: number | null;
  subcategoryIds?: number[] | null;
  categoryId?: number | null;
  categoryIds?: number[] | null;
  categoryGroupId?: number | null;
  categoryGroupIds?: number[] | null;
  brandId?: number | null;
  brandIds?: number[] | null;
  vendorId?: number | null;
  vendorIds?: number[] | null;
  q?: string;
  status?: ReplenishmentLevelStatus | 'all' | null;
  includeHidden?: boolean;
  page?: number;
  pageSize?: number;
}

function replenishmentStoreParam(f: { storeId?: number | null; storeIds?: number[] | null }) {
  if (f.storeIds && f.storeIds.length > 0) return f.storeIds.join(',');
  if (f.storeId != null) return String(f.storeId);
  return undefined;
}

function levelsParams(f: ReplenishmentLevelsFilters) {
  const params: Record<string, string | number> = {};
  const storeParam = replenishmentStoreParam(f);
  if (storeParam !== undefined) params.store_id = storeParam;
  const productParam = csvIdParam(f.productIds, f.productId);
  const subcategoryParam = csvIdParam(f.subcategoryIds, f.subcategoryId);
  const categoryParam = csvIdParam(f.categoryIds, f.categoryId);
  const categoryGroupParam = csvIdParam(f.categoryGroupIds, f.categoryGroupId);
  const brandParam = csvIdParam(f.brandIds, f.brandId);
  const vendorParam = csvIdParam(f.vendorIds, f.vendorId);
  if (productParam !== undefined) params.product_id = productParam;
  if (subcategoryParam !== undefined) params.subcategory_id = subcategoryParam;
  if (categoryParam !== undefined) params.category_id = categoryParam;
  if (categoryGroupParam !== undefined) params.category_group_id = categoryGroupParam;
  if (brandParam !== undefined) params.brand_id = brandParam;
  if (vendorParam !== undefined) params.vendor_id = vendorParam;
  if (f.q) params.q = f.q;
  if (f.status && f.status !== 'all') params.status = f.status;
  if (f.includeHidden) params.include_hidden = 1;
  if (f.page) params.page = f.page;
  if (f.pageSize) params.page_size = f.pageSize;
  return params;
}

export function useReplenishmentLevels(f: ReplenishmentLevelsFilters) {
  return useQuery({
    queryKey: [
      'bi', 'replenishment-levels',
      replenishmentStoreParam(f) ?? null,
      csvIdKey(f.productIds, f.productId),
      csvIdKey(f.subcategoryIds, f.subcategoryId),
      csvIdKey(f.categoryIds, f.categoryId),
      csvIdKey(f.categoryGroupIds, f.categoryGroupId),
      csvIdKey(f.brandIds, f.brandId),
      csvIdKey(f.vendorIds, f.vendorId),
      f.q ?? '',
      f.status ?? 'all',
      f.includeHidden ? 1 : 0,
      f.page ?? 1,
      f.pageSize ?? 50,
    ],
    queryFn: () =>
      get<ReplenishmentLevelsResponse>('/api/bi/inventory/replenishment/levels/', {
        params: levelsParams(f),
      }),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface ReplenishmentBasketsFilters {
  storeId?: number | null;
  storeIds?: number[] | null;
  productId?: number | null;
  productIds?: number[] | null;
  subcategoryId?: number | null;
  subcategoryIds?: number[] | null;
  categoryId?: number | null;
  categoryIds?: number[] | null;
  categoryGroupId?: number | null;
  categoryGroupIds?: number[] | null;
  brandId?: number | null;
  brandIds?: number[] | null;
  vendorId?: number | null;
  vendorIds?: number[] | null;
  q?: string;
  includeUnattributed?: boolean;
}

function basketsParams(f: ReplenishmentBasketsFilters) {
  const params: Record<string, string | number> = {};
  const storeParam = replenishmentStoreParam(f);
  if (storeParam !== undefined) params.store_id = storeParam;
  const productParam = csvIdParam(f.productIds, f.productId);
  const subcategoryParam = csvIdParam(f.subcategoryIds, f.subcategoryId);
  const categoryParam = csvIdParam(f.categoryIds, f.categoryId);
  const categoryGroupParam = csvIdParam(f.categoryGroupIds, f.categoryGroupId);
  const brandParam = csvIdParam(f.brandIds, f.brandId);
  const vendorParam = csvIdParam(f.vendorIds, f.vendorId);
  if (productParam !== undefined) params.product_id = productParam;
  if (subcategoryParam !== undefined) params.subcategory_id = subcategoryParam;
  if (categoryParam !== undefined) params.category_id = categoryParam;
  if (categoryGroupParam !== undefined) params.category_group_id = categoryGroupParam;
  if (brandParam !== undefined) params.brand_id = brandParam;
  if (vendorParam !== undefined) params.vendor_id = vendorParam;
  if (f.q) params.q = f.q;
  if (f.includeUnattributed === false) params.include_unattributed = 0;
  return params;
}

export function useReplenishmentBaskets(f: ReplenishmentBasketsFilters) {
  return useQuery({
    queryKey: [
      'bi', 'replenishment-baskets',
      replenishmentStoreParam(f) ?? null,
      csvIdKey(f.productIds, f.productId),
      csvIdKey(f.subcategoryIds, f.subcategoryId),
      csvIdKey(f.categoryIds, f.categoryId),
      csvIdKey(f.categoryGroupIds, f.categoryGroupId),
      csvIdKey(f.brandIds, f.brandId),
      csvIdKey(f.vendorIds, f.vendorId),
      f.q ?? '',
      f.includeUnattributed === false ? 0 : 1,
    ],
    queryFn: () =>
      get<ReplenishmentBasketsResponse>('/api/bi/inventory/replenishment/baskets/', {
        params: basketsParams(f),
      }),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export type TransferStatusFilter =
  | 'in_transit'
  | 'received'
  | 'partially_verified'
  | 'accepted'
  | 'packed'
  | 'all';

export interface ReplenishmentTransfersFilters extends BaseFilters {
  status?: TransferStatusFilter | null;
  q?: string;
  page?: number;
  pageSize?: number;
}

function transfersParams(f: ReplenishmentTransfersFilters) {
  return paramsFor(f, {
    ...(f.status && f.status !== 'all' ? { status: f.status } : {}),
    ...(f.q ? { q: f.q } : {}),
    ...(f.page ? { page: f.page } : {}),
    ...(f.pageSize ? { page_size: f.pageSize } : {}),
  });
}

export function useReplenishmentTransfers(f: ReplenishmentTransfersFilters) {
  return useQuery({
    queryKey: [
      'bi', 'replenishment-transfers',
      f.start, f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
      f.status ?? 'all',
      f.q ?? '',
      f.page ?? 1,
      f.pageSize ?? 25,
    ],
    queryFn: () =>
      get<ReplenishmentTransfersResponse>('/api/bi/inventory/replenishment/transfers/', {
        params: transfersParams(f),
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

// ---------------------------------------------------------------------------
// Stage 6 — Growth Deep Dive
// ---------------------------------------------------------------------------

export function useRfm(f: ListFilters) {
  return useQuery({
    queryKey: listKey('growth-rfm', f),
    queryFn: () =>
      get<RFMResponse>('/api/bi/growth/rfm/', {
        params: paramsFor(f, f.limit ? { limit: f.limit } : {}),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useCartAbandonment(f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'growth-abandonment',
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<CartAbandonmentResponse>('/api/bi/growth/abandonment/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useCouponPerformance(f: ListFilters) {
  return useQuery({
    queryKey: listKey('growth-coupons', f),
    queryFn: () =>
      get<CouponPerformanceResponse>('/api/bi/growth/coupons/', {
        params: paramsFor(f, f.limit ? { limit: f.limit } : {}),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useCouponDetail(couponId: number | null, f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'growth-coupon-detail',
      couponId,
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<CouponDetailResponse>(`/api/bi/growth/coupons/${couponId}/detail/`, {
        params: paramsFor(f),
      }),
    enabled: ENABLED_DEFAULT(f) && couponId != null,
  });
}

export function useReferralPerformance(f: ListFilters) {
  return useQuery({
    queryKey: listKey('growth-referrals', f),
    queryFn: () =>
      get<ReferralPerformanceResponse>('/api/bi/growth/referrals/', {
        params: paramsFor(f, f.limit ? { limit: f.limit } : {}),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useReferralDetail(referCodeId: number | null, f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'growth-referral-detail',
      referCodeId,
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<ReferralDetailResponse>(`/api/bi/growth/referrals/${referCodeId}/detail/`, {
        params: paramsFor(f),
      }),
    enabled: ENABLED_DEFAULT(f) && referCodeId != null,
  });
}

// ---------------------------------------------------------------------------
// Stage 7 — Orders & Strategic deepening
// ---------------------------------------------------------------------------

export function useOrdersSlaHeatmap(f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'orders-sla-heatmap',
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<OrdersSlaHeatmapResponse>('/api/bi/orders/sla/heatmap/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useOrdersDurations(f: BaseFilters & { sample?: number }) {
  return useQuery({
    queryKey: [
      'bi',
      'orders-funnel-durations',
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
      f.sample ?? null,
    ],
    queryFn: () =>
      get<OrdersDurationsResponse>('/api/bi/orders/funnel/durations/', {
        params: paramsFor(f, f.sample ? { sample: f.sample } : {}),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useRidersDistribution(f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'riders-distribution',
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<RidersDistributionResponse>('/api/bi/riders/distribution/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

// ---------------------------------------------------------------------------
// Stage 8 — Workforce / Payments / Feedback
// ---------------------------------------------------------------------------

export function useRosterCoverage(f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'roster-coverage',
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<RosterCoverageResponse>('/api/bi/roster/coverage/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function usePaymentsFunnel(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'payments-funnel', f.start, f.end],
    queryFn: () => get<PaymentsFunnelResponse>('/api/bi/payments/funnel/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function usePaymentsMandates(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'payments-mandates', f.start, f.end],
    queryFn: () =>
      get<PaymentsMandatesResponse>('/api/bi/payments/mandates/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useFeedbackNps(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'feedback-nps', f.start, f.end],
    queryFn: () => get<FeedbackNpsResponse>('/api/bi/feedback/nps/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
  });
}

// ---------------------------------------------------------------------------
// Stage 9 — Notifications + Anomaly Center + Audit
// ---------------------------------------------------------------------------

export function useNotificationsCampaigns(f: BaseFilters) {
  return useQuery({
    queryKey: ['bi', 'notif-campaigns', f.start, f.end],
    queryFn: () =>
      get<NotificationsCampaignsResponse>('/api/bi/notifications/campaigns/', {
        params: paramsFor(f),
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

interface AttributionFilters extends BaseFilters {
  windowHours?: number;
}
export function useNotificationsAttribution(f: AttributionFilters) {
  return useQuery({
    queryKey: ['bi', 'notif-attribution', f.start, f.end, f.windowHours ?? 24],
    queryFn: () =>
      get<NotificationsAttributionResponse>('/api/bi/notifications/attribution/', {
        params: paramsFor(f, { window_hours: f.windowHours ?? 24 }),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useAlertRules() {
  return useQuery({
    queryKey: ['bi', 'alert-rules'],
    queryFn: () => get<AlertRulesResponse>('/api/bi/alerts/rules/'),
  });
}

export async function createAlertRule(payload: {
  name: string;
  kind: AlertKind;
  severity: AlertSeverity;
  threshold_seconds: number;
  enabled?: boolean;
  config?: Record<string, unknown>;
}): Promise<AlertRule> {
  const res = await apiClient.post('/api/bi/alerts/rules/', payload);
  return res.data as AlertRule;
}

export async function patchAlertRule(
  id: number,
  patch: Partial<{
    name: string;
    kind: AlertKind;
    severity: AlertSeverity;
    threshold_seconds: number;
    enabled: boolean;
    config: Record<string, unknown>;
  }>
): Promise<AlertRule> {
  const res = await apiClient.patch(`/api/bi/alerts/rules/${id}/`, patch);
  return res.data as AlertRule;
}

export async function deleteAlertRule(id: number): Promise<void> {
  await apiClient.delete(`/api/bi/alerts/rules/${id}/`);
}

export function useAlertHistory(opts: { days?: number; onlyActive?: boolean } = {}) {
  const days = opts.days ?? 7;
  const onlyActive = !!opts.onlyActive;
  return useQuery({
    queryKey: ['bi', 'alert-history', days, onlyActive],
    queryFn: () =>
      get<AlertHistoryResponse>('/api/bi/alerts/history/', {
        params: { days, only_active: onlyActive ? 1 : 0 },
      }),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export async function recordAlertEvent(payload: {
  message: string;
  severity?: AlertSeverity;
  rule_id?: number;
  payload?: Record<string, unknown>;
}): Promise<AlertEvent> {
  const res = await apiClient.post('/api/bi/alerts/history/', payload);
  return res.data as AlertEvent;
}

export async function actOnAlertEvent(
  id: number,
  action: 'ack' | 'snooze' | 'unack',
  minutes?: number
): Promise<AlertEvent> {
  const res = await apiClient.post(`/api/bi/alerts/history/${id}/action/`, {
    action,
    ...(minutes ? { minutes } : {}),
  });
  return res.data as AlertEvent;
}

export function useAuditFeed(opts: { days?: number; sources?: string[]; limit?: number } = {}) {
  const days = opts.days ?? 14;
  const limit = opts.limit ?? 100;
  const sources = opts.sources && opts.sources.length ? opts.sources.join(',') : undefined;
  return useQuery({
    queryKey: ['bi', 'audit', days, limit, sources ?? null],
    queryFn: () =>
      get<AuditFeedResponse>('/api/bi/audit/', {
        params: {
          days,
          limit,
          ...(sources ? { sources } : {}),
        },
      }),
  });
}

// ---------------------------------------------------------------------------
// Stage 10 — Executive polish
// ---------------------------------------------------------------------------

interface ContributorsFilters extends BaseFilters {
  metric?: ContributorMetric;
  limit?: number;
}

export function useOverviewContributors(f: ContributorsFilters) {
  const metric = f.metric ?? 'gmv';
  const limit = f.limit ?? 5;
  return useQuery({
    queryKey: [
      'bi',
      'overview-contributors',
      metric,
      limit,
      f.start,
      f.end,
      f.compareStart ?? null,
      f.compareEnd ?? null,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<OverviewContributorsResponse>('/api/bi/overview/contributors/', {
        params: paramsFor(f, { metric, limit }),
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useTrialBalance(f: BaseFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'reports-trial-balance',
      f.start,
      f.end,
      f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    ],
    queryFn: () =>
      get<TrialBalanceResponse>('/api/bi/reports/trial-balance/', {
        params: paramsFor(f),
      }),
    enabled: ENABLED_DEFAULT(f),
  });
}

export function useReportSchedules() {
  return useQuery({
    queryKey: ['bi', 'reports-scheduled'],
    queryFn: () => get<ReportSchedulesResponse>('/api/bi/reports/scheduled/'),
  });
}

export async function createReportSchedule(payload: {
  name: string;
  page: string;
  frequency: ReportFrequency;
  fmt: ReportFormat;
  recipients?: string[];
  filters?: Record<string, unknown>;
  enabled?: boolean;
}): Promise<ReportSchedule> {
  const res = await apiClient.post('/api/bi/reports/scheduled/', payload);
  return res.data as ReportSchedule;
}

export async function patchReportSchedule(
  id: number,
  patch: Partial<{
    name: string;
    page: string;
    frequency: ReportFrequency;
    fmt: ReportFormat;
    recipients: string[];
    filters: Record<string, unknown>;
    enabled: boolean;
  }>
): Promise<ReportSchedule> {
  const res = await apiClient.patch(`/api/bi/reports/scheduled/${id}/`, patch);
  return res.data as ReportSchedule;
}

export async function deleteReportSchedule(id: number): Promise<void> {
  await apiClient.delete(`/api/bi/reports/scheduled/${id}/`);
}

/**
 * Stream `/inventory/ledger/export/` to a Blob and trigger a browser
 * download. We can't do this via React Query because the response is a
 * stream, not JSON.
 */
export async function downloadStockLedgerCsv(f: LedgerFilters): Promise<void> {
  const res = await apiClient.get('/api/bi/inventory/ledger/export/', {
    params: ledgerParams(f),
    responseType: 'blob',
  });
  const blob = new Blob([res.data], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `stock_ledger_${f.start.slice(0, 10)}_${f.end.slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ---------------------------------------------------------------------------
// Vendor profitability — Stage 12
//
// Catalog & Realized share a filter shape; Realized adds date/store. The
// hooks below mirror `useVendorProductSales` so the page can plug in the
// existing filter rail unchanged.
// ---------------------------------------------------------------------------

export interface VendorProfitabilityFilters {
  storeIds?: number[] | null;
  categoryIds?: number[] | null;
  subcategoryIds?: number[] | null;
  vendorIds?: number[] | null;
  brandIds?: number[] | null;
  q?: string;
  enabled?: boolean;
}

export interface VendorProfitabilityCatalogFilters extends VendorProfitabilityFilters {
  weightBasis?: 'stock' | 'equal';
  includeInactive?: boolean;
}

export interface VendorProfitabilityRealizedFilters extends VendorProfitabilityFilters {
  start: string;
  end: string;
}

export interface VendorProfitabilityQuadrantFilters extends VendorProfitabilityRealizedFilters {
  top?: number;
}

function pivotParams(f: VendorProfitabilityFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (f.storeIds && f.storeIds.length) params.store_id = f.storeIds.join(',');
  if (f.categoryIds && f.categoryIds.length) params.category_id = f.categoryIds.join(',');
  if (f.subcategoryIds && f.subcategoryIds.length) params.subcategory_id = f.subcategoryIds.join(',');
  if (f.vendorIds && f.vendorIds.length) params.vendor_id = f.vendorIds.join(',');
  if (f.brandIds && f.brandIds.length) params.brand_id = f.brandIds.join(',');
  const q = (f.q ?? '').trim();
  if (q) params.q = q;
  return params;
}

function pivotKey(f: VendorProfitabilityFilters): (string | number | null)[] {
  // Sort each id list so the cache stays consistent across user-side reorders.
  const sortedKey = (xs?: number[] | null) =>
    xs && xs.length ? [...xs].sort((a, b) => a - b).join(',') : '';
  return [
    sortedKey(f.storeIds),
    sortedKey(f.categoryIds),
    sortedKey(f.subcategoryIds),
    sortedKey(f.vendorIds),
    sortedKey(f.brandIds),
    (f.q ?? '').trim() || '',
  ];
}

export function useVendorProfitabilityCatalog(f: VendorProfitabilityCatalogFilters) {
  const enabled = f.enabled !== false;
  const weightBasis = f.weightBasis ?? 'stock';
  const includeInactive = f.includeInactive ?? false;
  return useQuery({
    queryKey: [
      'bi', 'vendor-profitability', 'catalog',
      ...pivotKey(f),
      weightBasis, includeInactive,
    ],
    queryFn: () =>
      get<VendorProfitabilityResponse>('/api/bi/vendors/profitability/catalog/', {
        params: {
          ...pivotParams(f),
          weight_basis: weightBasis,
          ...(includeInactive ? { include_inactive: 1 } : {}),
        },
      }),
    enabled,
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useVendorProfitabilityRealized(f: VendorProfitabilityRealizedFilters) {
  const enabled = f.enabled !== false && Boolean(f.start && f.end);
  return useQuery({
    queryKey: [
      'bi', 'vendor-profitability', 'realized',
      f.start, f.end, ...pivotKey(f),
    ],
    queryFn: () =>
      get<VendorProfitabilityResponse>('/api/bi/vendors/profitability/realized/', {
        params: {
          ...biRangeParams(f),
          ...pivotParams(f),
        },
      }),
    enabled,
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useVendorProfitabilityQuadrant(f: VendorProfitabilityQuadrantFilters) {
  const enabled = f.enabled !== false && Boolean(f.start && f.end);
  const top = f.top ?? 8;
  return useQuery({
    queryKey: [
      'bi', 'vendor-profitability', 'quadrant',
      f.start, f.end, ...pivotKey(f), top,
    ],
    queryFn: () =>
      get<VendorProfitabilityQuadrantResponse>('/api/bi/vendors/profitability/quadrant/', {
        params: {
          ...biRangeParams(f),
          ...pivotParams(f),
          top,
        },
      }),
    enabled,
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface VendorSubcategoryDecisionFilters {
  vendorIds?: number[] | null;
  subcategoryIds?: number[] | null;
  enabled?: boolean;
}

export function useVendorSubcategoryDecisions(f: VendorSubcategoryDecisionFilters = {}) {
  const enabled = f.enabled !== false;
  return useQuery({
    queryKey: [
      'bi', 'vendor-profitability', 'decisions',
      f.vendorIds && f.vendorIds.length ? [...f.vendorIds].sort().join(',') : '',
      f.subcategoryIds && f.subcategoryIds.length ? [...f.subcategoryIds].sort().join(',') : '',
    ],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (f.vendorIds && f.vendorIds.length) params.vendor_id = f.vendorIds.join(',');
      if (f.subcategoryIds && f.subcategoryIds.length) {
        params.subcategory_id = f.subcategoryIds.join(',');
      }
      return get<VendorSubcategoryDecisionListResponse>(
        '/api/bi/vendors/profitability/decisions/',
        { params },
      );
    },
    enabled,
  });
}

export interface UpsertVendorSubcategoryDecisionPayload {
  vendor_id: number;
  subcategory_id: number;
  decision: VendorDecision;
  note?: string;
}

export async function upsertVendorSubcategoryDecision(
  payload: UpsertVendorSubcategoryDecisionPayload,
): Promise<VendorSubcategoryDecisionRow> {
  const res = await apiClient.post(
    '/api/bi/vendors/profitability/decisions/',
    payload,
  );
  return res.data as VendorSubcategoryDecisionRow;
}

export async function patchVendorSubcategoryDecision(
  id: number,
  patch: Partial<{ decision: VendorDecision; note: string }>,
): Promise<VendorSubcategoryDecisionRow> {
  const res = await apiClient.patch(
    `/api/bi/vendors/profitability/decisions/${id}/`,
    patch,
  );
  return res.data as VendorSubcategoryDecisionRow;
}

export async function deleteVendorSubcategoryDecision(id: number): Promise<void> {
  await apiClient.delete(`/api/bi/vendors/profitability/decisions/${id}/`);
}

/**
 * Convenience wrappers — invalidate every profitability cache so the
 * page picks up the new decision row on the next render.
 */
function invalidateProfitabilityCaches(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['bi', 'vendor-profitability'] });
}

export function useUpsertVendorSubcategoryDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: upsertVendorSubcategoryDecision,
    onSuccess: () => invalidateProfitabilityCaches(qc),
  });
}

export function usePatchVendorSubcategoryDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: {
      id: number;
      patch: Partial<{ decision: VendorDecision; note: string }>;
    }) => patchVendorSubcategoryDecision(id, patch),
    onSuccess: () => invalidateProfitabilityCaches(qc),
  });
}

export function useDeleteVendorSubcategoryDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteVendorSubcategoryDecision,
    onSuccess: () => invalidateProfitabilityCaches(qc),
  });
}

// ---------------------------------------------------------------------------
// Products — merchandising analytics & demand forecast
// ---------------------------------------------------------------------------

/** Taxonomy filter slice shared by every product endpoint. */
export interface ProductTaxonomyFilters {
  storeId?: number | null;
  storeIds?: number[] | null;
  vendorIds?: number[] | null;
  brandIds?: number[] | null;
  categoryGroupIds?: number[] | null;
  categoryIds?: number[] | null;
  subcategoryIds?: number[] | null;
  productIds?: number[] | null;
  q?: string;
}

function productTaxonomyParams(f: ProductTaxonomyFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (f.storeIds && f.storeIds.length) params.store_id = f.storeIds.join(',');
  else if (f.storeId != null) params.store_id = String(f.storeId);
  const add = (key: string, ids: number[] | null | undefined) => {
    if (ids && ids.length) params[key] = ids.join(',');
  };
  add('vendor_id', f.vendorIds);
  add('brand_id', f.brandIds);
  add('category_group_id', f.categoryGroupIds);
  add('category_id', f.categoryIds);
  add('subcategory_id', f.subcategoryIds);
  add('product_id', f.productIds);
  if (f.q && f.q.trim()) params.q = f.q.trim();
  return params;
}

function productTaxonomyKey(f: ProductTaxonomyFilters): (string | number | null)[] {
  const k = (ids: number[] | null | undefined) =>
    ids && ids.length ? [...ids].sort((a, b) => a - b).join(',') : '';
  return [
    f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
    k(f.vendorIds),
    k(f.brandIds),
    k(f.categoryGroupIds),
    k(f.categoryIds),
    k(f.subcategoryIds),
    k(f.productIds),
    f.q?.trim() || '',
  ];
}

export interface ProductPerformanceFilters extends BaseFilters, ProductTaxonomyFilters {
  groupBy: ProductGroupBy;
  limit?: number;
}

export function useProductPerformance(f: ProductPerformanceFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-performance',
      f.start, f.end,
      f.compareStart ?? null, f.compareEnd ?? null,
      f.groupBy, f.limit ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductPerformanceResponse>('/api/bi/products/performance/', {
        params: {
          ...paramsFor(f, { group_by: f.groupBy, ...(f.limit ? { limit: f.limit } : {}) }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface ProductTimeseriesFilters extends BaseFilters, ProductTaxonomyFilters {
  granularity?: Granularity;
}

export function useProductTimeseries(f: ProductTimeseriesFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-timeseries',
      f.start, f.end, f.granularity ?? 'day',
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductTimeseriesResponse>('/api/bi/products/timeseries/', {
        params: {
          ...paramsFor(f, { granularity: f.granularity ?? 'day' }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface ProductForecastFilters extends ProductTaxonomyFilters {
  lookback?: number;
  horizon?: number;
  enabled?: boolean;
}

export function useProductForecast(f: ProductForecastFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-forecast',
      f.lookback ?? 60, f.horizon ?? 14,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductForecastResponse>('/api/bi/products/forecast/', {
        params: {
          ...(f.lookback ? { lookback: f.lookback } : {}),
          ...(f.horizon ? { horizon: f.horizon } : {}),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: f.enabled !== false,
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface ProductQuadrantFilters extends BaseFilters, ProductTaxonomyFilters {
  groupBy: ProductGroupBy;
  limit?: number;
}

export function useProductQuadrant(f: ProductQuadrantFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-quadrant',
      f.start, f.end, f.groupBy, f.limit ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductQuadrantResponse>('/api/bi/products/profitability/quadrant/', {
        params: {
          ...paramsFor(f, { group_by: f.groupBy, ...(f.limit ? { limit: f.limit } : {}) }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface ProductDrilldownFilters extends BaseFilters, ProductTaxonomyFilters {
  limit?: number;
}

export function useProductDrilldown(f: ProductDrilldownFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-drilldown',
      f.start, f.end, f.limit ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductDrilldownResponse>('/api/bi/products/drilldown/', {
        params: {
          ...paramsFor(f, { ...(f.limit ? { limit: f.limit } : {}) }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface ProductRepeatFilters extends BaseFilters, ProductTaxonomyFilters {
  page?: number;
  pageSize?: number;
}

export function useProductRepeat(f: ProductRepeatFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-repeat',
      f.start, f.end, f.page ?? 1, f.pageSize ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductRepeatResponse>('/api/bi/products/repeat/', {
        params: {
          ...paramsFor(f, { page: f.page ?? 1, ...(f.pageSize ? { page_size: f.pageSize } : {}) }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

/** Fetch the full (unpaginated) re-ordered SKU list for CSV export. */
export async function fetchProductRepeatExport(f: ProductRepeatFilters): Promise<ProductRepeatResponse> {
  return get<ProductRepeatResponse>('/api/bi/products/repeat/', {
    params: { ...paramsFor(f, { export: 1 }), ...productTaxonomyParams(f) },
  });
}

export type ProductStoreComparisonFilters = BaseFilters & ProductTaxonomyFilters;

export function useProductStores(f: ProductStoreComparisonFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-stores',
      f.start, f.end,
      f.compareStart ?? null, f.compareEnd ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductStoreComparisonResponse>('/api/bi/products/stores/', {
        params: {
          ...paramsFor(f),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface ProductAffinityFilters extends BaseFilters, ProductTaxonomyFilters {
  minSupport?: number;
  page?: number;
  pageSize?: number;
}

function affinityExtras(f: ProductAffinityFilters): Record<string, string | number | undefined> {
  return {
    ...(f.minSupport ? { min_support: f.minSupport } : {}),
  };
}

export function useProductAffinity(f: ProductAffinityFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-affinity',
      f.start, f.end, f.minSupport ?? null, f.page ?? 1, f.pageSize ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductAffinityResponse>('/api/bi/products/affinity/', {
        params: {
          ...paramsFor(f, {
            ...affinityExtras(f),
            page: f.page ?? 1,
            ...(f.pageSize ? { page_size: f.pageSize } : {}),
          }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

/** Fetch the full (unpaginated) basket-affinity pairs for CSV export. */
export async function fetchProductAffinityExport(f: ProductAffinityFilters): Promise<ProductAffinityResponse> {
  return get<ProductAffinityResponse>('/api/bi/products/affinity/', {
    params: { ...paramsFor(f, { ...affinityExtras(f), export: 1 }), ...productTaxonomyParams(f) },
  });
}

export interface ProductVelocityFilters extends BaseFilters, ProductTaxonomyFilters {
  fastPage?: number;
  deadPage?: number;
  pageSize?: number;
}

export function useProductVelocity(f: ProductVelocityFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-velocity',
      f.start, f.end, f.fastPage ?? 1, f.deadPage ?? 1, f.pageSize ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductVelocityResponse>('/api/bi/products/velocity/', {
        params: {
          ...paramsFor(f, {
            fast_page: f.fastPage ?? 1,
            dead_page: f.deadPage ?? 1,
            ...(f.pageSize ? { page_size: f.pageSize } : {}),
          }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

/** Fetch the full (unpaginated) velocity lists (fast movers + dead stock) for CSV export. */
export async function fetchProductVelocityExport(f: ProductVelocityFilters): Promise<ProductVelocityResponse> {
  return get<ProductVelocityResponse>('/api/bi/products/velocity/', {
    params: { ...paramsFor(f, { export: 1 }), ...productTaxonomyParams(f) },
  });
}

// ---------------------------------------------------------------------------
// Geo product analytics — ward-wise sales map
// ---------------------------------------------------------------------------

/**
 * Ward boundary polygons (GeoJSON). Static reference data — cached for a
 * long time client-side; the server also caches aggressively.
 */
export function useWardsGeo(opts: { all?: boolean; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['bi', 'wards-geo', opts.all ?? false],
    queryFn: () =>
      get<WardsGeoResponse>('/api/bi/wards/', {
        params: opts.all ? { all: 1 } : {},
      }),
    enabled: opts.enabled !== false,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export type ProductWardSalesFilters = BaseFilters & ProductTaxonomyFilters;

export function useProductWardSales(f: ProductWardSalesFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-ward-sales',
      f.start, f.end,
      f.compareStart ?? null, f.compareEnd ?? null,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<ProductWardSalesResponse>('/api/bi/products/wards/', {
        params: {
          ...paramsFor(f),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export interface WardDetailFilters extends BaseFilters, ProductTaxonomyFilters {
  wardId: number | null;
}

export function useWardDetail(f: WardDetailFilters) {
  return useQuery({
    queryKey: [
      'bi', 'products-ward-detail',
      f.wardId, f.start, f.end,
      ...productTaxonomyKey(f),
    ],
    queryFn: () =>
      get<WardDetailResponse>('/api/bi/products/wards/detail/', {
        params: {
          ...paramsFor(f, { ward_id: f.wardId ?? undefined }),
          ...productTaxonomyParams(f),
        },
      }),
    enabled: ENABLED_DEFAULT(f) && f.wardId != null,
  });
}

// ---------------------------------------------------------------------------
// Shrinkage analytics — /api/bi/shrinkage/*
// ---------------------------------------------------------------------------

function shrinkageKey(name: string, f: BaseFilters) {
  return [
    'bi',
    name,
    f.start,
    f.end,
    f.storeIds && f.storeIds.length ? f.storeIds.join(',') : f.storeId ?? null,
  ];
}

export function useShrinkageOverview(f: BaseFilters) {
  return useQuery({
    queryKey: shrinkageKey('shrinkage-overview', f),
    queryFn: () =>
      get<ShrinkageOverviewResponse>('/api/bi/shrinkage/overview/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useShrinkageHotspots(f: BaseFilters) {
  return useQuery({
    queryKey: shrinkageKey('shrinkage-hotspots', f),
    queryFn: () =>
      get<ShrinkageHotspotsResponse>('/api/bi/shrinkage/hotspots/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useShrinkageVariance(f: BaseFilters) {
  return useQuery({
    queryKey: shrinkageKey('shrinkage-variance', f),
    queryFn: () =>
      get<ShrinkageVarianceResponse>('/api/bi/shrinkage/variance/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useShrinkageAttribution(f: BaseFilters) {
  return useQuery({
    queryKey: shrinkageKey('shrinkage-attribution', f),
    queryFn: () =>
      get<ShrinkageAttributionResponse>('/api/bi/shrinkage/attribution/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}

export function useShrinkageAnomalies(f: BaseFilters) {
  return useQuery({
    queryKey: shrinkageKey('shrinkage-anomalies', f),
    queryFn: () =>
      get<ShrinkageAnomaliesResponse>('/api/bi/shrinkage/anomalies/', { params: paramsFor(f) }),
    enabled: ENABLED_DEFAULT(f),
    refetchInterval: REFETCH_INTERVAL,
  });
}
